// CockpitView.tsx — scenario editor + results (view 1). Scenario state, run results and
// the scenario-switcher machinery are owned by app.tsx (see its header comment) and
// passed down here as props, so #vergleich can read the same results without this view
// needing to be mounted. Cockpit edits exactly ONE scenario at a time — the
// ScenarioSwitcher above the editor picks which of the N scenarios that is.
//
// AUTO-COMPUTE (user feedback: "berechne immer alle Szenarien"): there is no "Berechnen"
// button here anymore — app.tsx's auto-compute effect (debounced ~200ms, memoized by
// lib/autoCompute.ts's hashScenarioConfig) keeps every scenario's `results`
// entry up to date as its own inputs change, including the ACTIVE one this view renders.
// `run.pending` (set while a RECOMPUTE, as opposed to the first-ever compute, is in
// flight) drives the small "wird aktualisiert…" indicator below — the previous result
// stays fully visible the whole time, never blanked mid-recompute.
import { useEffect, useState } from 'preact/hooks';
import { ScenarioEditor } from '../components/ScenarioEditor.js';
import { ResultsPanel } from '../components/ResultsPanel.js';
import { ScenarioSwitcher } from '../components/ScenarioSwitcher.js';
import { ScenarioLibraryBar } from '../components/ScenarioLibraryBar.js';
import { ApiError, getFunds } from '../lib/api.js';
import type { RunResult, ScenarioState } from '../lib/scenario.js';
import type { View } from '../lib/router.js';
import type { RealTermsSettings } from '../lib/realTerms.js';
import type { PersistedState } from '../lib/storage.js';
import type { FundInfo } from '../lib/types.js';
import type { MonteCarloSettings } from '../lib/montecarlo.js';

export interface CockpitViewProps {
  scenarioState: PersistedState;
  /** Keyed by scenario id (lib/storage.ts's ScenarioListEntry.id), not a fixed A/B pair —
   * app.tsx owns the map and drops an id's entry when that scenario is deleted. */
  results: Record<string, RunResult>;
  onUpdateScenario: (id: string, scenario: ScenarioState) => void;
  onSetActiveScenario: (id: string) => void;
  onAddScenario: () => void;
  onRenameScenario: (id: string, label: string) => void;
  onDuplicateScenario: (id: string) => void;
  onDeleteScenario: (id: string) => void;
  onResetScenario: (id: string) => void;
  /** Bypasses the auto-compute cache and forces an immediate recompute of `id` — the
   * error banner's "Erneut versuchen" fallback (e.g. after a transient offline/network
   * failure that no config change would otherwise re-trigger). Not a general "Berechnen"
   * button; auto-compute already handles every ordinary edit. */
  onRetryScenario: (id: string) => void;
  onNavigate: (view: View) => void;
  realTerms: RealTermsSettings;
  onRealTermsChange: (settings: RealTermsSettings) => void;
}

const IDLE_RUN_RESULT: RunResult = { status: 'idle' };

export function CockpitView({
  scenarioState,
  results,
  onUpdateScenario,
  onSetActiveScenario,
  onAddScenario,
  onRenameScenario,
  onDuplicateScenario,
  onDeleteScenario,
  onResetScenario,
  onRetryScenario,
  realTerms,
  onRealTermsChange,
}: CockpitViewProps) {
  const [funds, setFunds] = useState<FundInfo[]>([]);
  const [fundsLoading, setFundsLoading] = useState(true);
  const [fundsError, setFundsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setFundsLoading(true);
    getFunds()
      .then((res) => {
        if (cancelled) return;
        setFunds(res.funds);
        setFundsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFundsError(err instanceof ApiError ? err.message : 'Unbekannter Fehler beim Laden der Fonds.');
        setFundsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeEntry = scenarioState.scenarios.find((e) => e.id === scenarioState.activeId) ?? scenarioState.scenarios[0];
  const activeResult = activeEntry ? (results[activeEntry.id] ?? IDLE_RUN_RESULT) : IDLE_RUN_RESULT;

  if (!activeEntry) return null;

  return (
    <div>
      <div class="card scenario-controls">
        <ScenarioSwitcher
          scenarios={scenarioState.scenarios}
          activeId={activeEntry.id}
          onSelect={onSetActiveScenario}
          onAdd={onAddScenario}
          onRename={onRenameScenario}
          onDuplicate={onDuplicateScenario}
          onDelete={onDeleteScenario}
          onReset={onResetScenario}
        />
        <div class="scenario-toolbar-row">
          <ScenarioLibraryBar
            activeScenario={activeEntry.scenario}
            onLoad={(scenario) => onUpdateScenario(activeEntry.id, scenario)}
            onRenameActive={(label) => onRenameScenario(activeEntry.id, label)}
          />
        </div>
      </div>

      <div class="card">
        <h2>{activeEntry.scenario.label}</h2>
        <ScenarioEditor
          scenario={activeEntry.scenario}
          onChange={(scenario) => onUpdateScenario(activeEntry.id, scenario)}
          funds={funds}
          fundsLoading={fundsLoading}
          fundsError={fundsError}
        />
      </div>

      <SingleResult
        run={activeResult}
        onRetry={() => onRetryScenario(activeEntry.id)}
        realTerms={realTerms}
        onRealTermsChange={onRealTermsChange}
        monteCarloSettings={{
          volatilityPercent: activeEntry.scenario.forecast.mcVolatilityPercent,
          paths: activeEntry.scenario.forecast.mcPaths,
          seed: activeEntry.scenario.forecast.mcSeed,
        }}
        onMonteCarloSettingsChange={(mc) =>
          onUpdateScenario(activeEntry.id, {
            ...activeEntry.scenario,
            forecast: {
              ...activeEntry.scenario.forecast,
              mcVolatilityPercent: mc.volatilityPercent,
              mcPaths: mc.paths,
              mcSeed: mc.seed,
            },
          })
        }
      />
    </div>
  );
}

function SingleResult({
  run,
  onRetry,
  monteCarloSettings,
  onMonteCarloSettingsChange,
  realTerms,
  onRealTermsChange,
}: {
  run: RunResult;
  onRetry: () => void;
  monteCarloSettings: MonteCarloSettings;
  onMonteCarloSettingsChange: (settings: MonteCarloSettings) => void;
  realTerms: RealTermsSettings;
  onRealTermsChange: (settings: RealTermsSettings) => void;
}) {
  if (run.status === 'idle') {
    // Only a transient flash before auto-compute's ~200ms debounce fires on mount — no
    // manual action needed here anymore (user feedback: results should just appear).
    return (
      <div class="empty-state">
        <span class="spinner" /> Wird berechnet…
      </div>
    );
  }
  if (run.status === 'loading') {
    return (
      <div class="empty-state">
        <span class="spinner" /> Wird berechnet…
      </div>
    );
  }
  if (run.status === 'error') {
    return (
      <div class="banner banner-error" role="alert">
        <strong>Fehler</strong>
        <span>{run.error}</span>
        <div class="btn-row" style={{ marginTop: '0.5rem' }}>
          <button type="button" class="btn" onClick={onRetry}>
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }
  if (!run.result || !run.mode) return null;
  return (
    <>
      {run.pending ? (
        <p class="field-hint auto-compute-pending">
          <span class="spinner" /> Wird aktualisiert…
        </p>
      ) : null}
      <ResultsPanel
        result={run.result}
        mode={run.mode}
        meta={run.meta}
        forecastConfig={run.config}
        monteCarloSettings={monteCarloSettings}
        onMonteCarloSettingsChange={onMonteCarloSettingsChange}
        realTerms={realTerms}
        onRealTermsChange={onRealTermsChange}
      />
    </>
  );
}
